import {
  type AerieEndedEvent,
  type AerieGateEvent,
  type AerieRejected,
  RAID_LEVEL,
  RAID_MAX_PLAYERS,
  RAID_MIN_PLAYERS,
  type ShefiraStateEvent,
} from '@shefira/shared';

import { uiFormat, uiText } from './text.js';

/**
 * The words of Shefira's Aire (ADR 0068), from what the server sent: the
 * boss bar's line, the crevasse's window, why it stays shut, how the fight
 * ended. Pure, so the view models are tested without a page.
 */

/** Why the crevasse stays shut, in plain words; `name` is who holds it back. */
export function aerieRefusalText(refusal: AerieRejected): string {
  const name = refusal.name ?? '';
  switch (refusal.reason) {
    case 'invalid':
      return uiText('Demande invalide.');
    case 'no-gate':
      return uiText('La crevasse n’est pas ici.');
    case 'too-far':
      return uiText('Approche-toi de la crevasse.');
    case 'dead':
      return uiText('Pas tant que tu es à terre.');
    case 'in-combat':
      return uiText('Tu es en plein combat.');
    case 'no-quest':
      return uiText('La Compagnie ne t’attend pas ici : la dernière garde n’est pas en cours.');
    case 'not-leader':
      return uiText('Seul le chef du groupe, ou d’une alliance, lance le raid.');
    case 'too-few':
      return uiFormat('Il faut au moins {min} joueurs pour le raid.', { min: RAID_MIN_PLAYERS });
    case 'too-many':
      return uiFormat('Pas plus de {max} joueurs dans le raid.', { max: RAID_MAX_PLAYERS });
    case 'level':
      return name === ''
        ? uiFormat('Tout le raid doit être niveau {level}.', { level: RAID_LEVEL })
        : uiFormat('{name} n’est pas encore niveau {level}.', { name, level: RAID_LEVEL });
    case 'not-stood':
      return name === ''
        ? uiText('Chacun doit d’abord avoir mené la dernière garde de la Compagnie.')
        : uiFormat('{name} n’a pas encore mené la dernière garde de la Compagnie.', { name });
    case 'absent':
      return name === ''
        ? uiText('Tout le raid doit se tenir à l’entrée de l’Aire.')
        : uiFormat('{name} n’est pas à l’entrée de l’Aire.', { name });
    case 'busy':
      return uiText('Quelqu’un du raid est déjà ailleurs : réessaie dans un instant.');
  }
}

/** One raider's line in the crevasse's window. */
export interface GateRow {
  name: string;
  level: string;
  /** Everything the raid asks of them is met. */
  ready: boolean;
  /** What is missing, or « Prêt ». */
  note: string;
}

export interface GateView {
  story: { open: boolean; note: string };
  raid: {
    rows: GateRow[];
    /** This player may press « Lancer le raid » now. */
    canLaunch: boolean;
    note: string;
  };
}

/** The window at the crevasse, from its `aerie_gate` answer. */
export function gateView(gate: AerieGateEvent): GateView {
  const rows = gate.raid.members.map((member): GateRow => {
    const missing: string[] = [];
    if (!member.present) missing.push(uiText('Absent'));
    if (member.level < RAID_LEVEL)
      missing.push(uiFormat('Niveau {level} requis', { level: RAID_LEVEL }));
    if (!member.stood) missing.push(uiText('Dernière garde à mener'));
    return {
      name: member.name,
      level: uiFormat('niv. {level}', { level: member.level }),
      ready: missing.length === 0,
      note: missing.length === 0 ? uiText('Prêt') : missing.join(' · '),
    };
  });
  const refusal = gate.raid.refusal;
  return {
    story: {
      open: gate.story,
      note: gate.story
        ? uiText(
            'Entre seul avec la Compagnie : Ser Givrecoeur et les cinq recrues se battent à tes côtés.',
          )
        : uiText('L’histoire de la dernière garde n’est pas en cours pour toi.'),
    },
    raid: {
      rows,
      canLaunch: refusal === null && gate.raid.leader,
      note:
        refusal === null
          ? uiFormat('Le raid est prêt : {count} joueurs.', { count: rows.length })
          : refusal === 'too-few' && rows.length === 0
            ? uiFormat(
                'Forme un groupe de {min} à 5 joueurs, ou une alliance de deux groupes (jusqu’à {max}).',
                {
                  min: RAID_MIN_PLAYERS,
                  max: RAID_MAX_PLAYERS,
                },
              )
            : aerieRefusalText({ reason: refusal }),
    },
  };
}

/** Her phase, as the boss bar names it. */
export function phaseLabel(phase: ShefiraStateEvent['phase']): string {
  switch (phase) {
    case 'ground':
      return uiText('Au sol');
    case 'flight':
      return uiText('En vol');
    case 'enraged':
      return uiText('Enragée');
  }
}

/** What the boss bar shows of her fight under her health, besides the phase marks. */
export interface AerieBarInfo {
  phase: ShefiraStateEvent['phase'];
  airborne: boolean;
  /** While she flies: whole seconds until she comes down at the latest. */
  landsIn: number | null;
  harpoons: number;
  mode: ShefiraStateEvent['mode'];
  players: number;
}

/** The line under her bar: phase, out of reach and when she lands, harpoons, the raid's size. */
export function aerieBarLine(info: AerieBarInfo): string {
  const parts = [phaseLabel(info.phase)];
  if (info.airborne) {
    parts.push(
      info.landsIn === null
        ? uiText('Hors d’atteinte')
        : uiFormat('Hors d’atteinte · se pose dans {seconds} s', { seconds: info.landsIn }),
    );
    if (info.phase === 'flight') {
      parts.push(uiFormat('Harpons {count}/2', { count: Math.min(2, info.harpoons) }));
    }
  }
  if (info.mode === 'raid') parts.push(uiFormat('Raid de {count}', { count: info.players }));
  return parts.join(' · ');
}

/** The banner at the end of the fight. */
export function endedTitle(outcome: AerieEndedEvent['outcome']): string {
  switch (outcome) {
    case 'victory':
      return uiText('Shefira est vaincue !');
    case 'fled':
      return uiText('Shefira s’enfuit, l’épée au flanc');
    case 'defeat':
      return uiText('La Compagnie est tombée');
  }
}

export function endedNote(ended: Pick<AerieEndedEvent, 'outcome' | 'lootTaken'>): string {
  switch (ended.outcome) {
    case 'victory':
      return ended.lootTaken === true
        ? uiText('Tu as déjà eu ton butin de Shefira cette semaine.')
        : uiText('Ton butin t’attend à ses pieds.');
    case 'fled':
      return uiText('Ser Givrecoeur a planté son épée. La dernière garde est tenue.');
    case 'defeat':
      return uiText('Relève-toi et reviens la défier.');
  }
}

export function exitCountdown(seconds: number): string {
  return uiFormat('Retour à la Brèche dans {seconds} s', { seconds });
}
